import { RefObject } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Layer } from '@methanesat/maps';
import { booleanWithin, polygon } from '@turf/turf';
import { Geometry } from 'geojson';

import { maxZoomOGITiles, PICKING_RADIUS_PIXELS, ZOOM_THRESHOLDS } from '../consts';

import {
    resetSelectedFeatureParams,
    selectFlooredZoom,
    setActiveCapture,
    setEmissionsMapViewState,
    setSelectedFeatureParams,
    setSelectedOGIFeature,
    SLOW_selectEmissionsMapViewState
} from '../reducers';

import {
    CapturePlusCount,
    isClusteredPointInfrastructureInfo,
    isPlumeEmissionInfo,
    isPointInfrastructureInfo,
    isRasterInfo,
    isTargetInfo,
    MSatPickInfo,
    PickInfoToPrioritize,
    ViewStatePropsQueryString,
    EmissionDrawerCardData
} from '../types';
import { DeckGLRefWithWidth } from '../types/DeckGLExtension';
import { getEmissionSelectionParams, getValidSortedPickInfos, pickMultiplePrioritizedFilteredLayers } from './pickInfo';

import { formatDateForUrl } from './stringFormatters';

/**
 * Centralized function to handle all map click behavior.
 *
 * IMPORTANT NOTE: This is used on the main page and the single collection page. Please check both
 * if updating any functionality.
 *
 * Usage in the page below: useOnMapClick = makeUseOnMapClick(deck, layers)
 * `useMapClick` is then passed to `EmissionsPageMap` to create the function that is actually
 * called when the user clicks the map.
 *
 * TODO: this function is long and complicated (chooses infos, manages
 * repositioning map - which is broken - sets active capture, selected feature, etc, etc)
 * It should be split, refactored, etc.
 * See https://methanesat.atlassian.net/browse/DP-5699
 *
 */
export function makeUseOnMapClick(
    handleMapInfo: (
        info: PickInfoToPrioritize[] | undefined,
        clickedNoData: boolean,
        showNoDataDrawer: boolean,
        addMultipleHighlights?: boolean
    ) => void,
    rightWidthOffset: number = 0
) {
    return function useOnMapClick(
        deck: RefObject<DeckGLRefWithWidth | null>,
        layers: Layer[],
        visibleCaptures: CapturePlusCount[]
    ) {
        const dispatch = useDispatch();

        const flooredZoom = useSelector(selectFlooredZoom);
        const viewState = useSelector(SLOW_selectEmissionsMapViewState);

        return async function onMapClick(info: MSatPickInfo) {
            if (!deck?.current) return;
            // reset the selected features in the redux store
            dispatch(resetSelectedFeatureParams());
            dispatch(setSelectedOGIFeature(null));

            // get pickinfo from all layers with features
            // at the coordinates the user clicked
            const layersOfInfoAtPoint: PickInfoToPrioritize[] = deck.current.pickMultipleObjects({
                x: info.x,
                y: info.y,
                depth: layers.length,
                radius: PICKING_RADIUS_PIXELS
            }) as PickInfoToPrioritize[];

            // keep infos with valid objects and bitmaps
            const filteredLayersOfInfoAtPoint = layersOfInfoAtPoint.filter((info) => {
                return (typeof info.object === 'object' && info.object) || info.bitmap;
            });

            /** layers at point sorted by priority  */
            const sortedPickInfos: PickInfoToPrioritize[] | undefined = await getValidSortedPickInfos(
                filteredLayersOfInfoAtPoint,
                viewState.zoom,
                dispatch
            );
            const prioritizedInfo = sortedPickInfos && sortedPickInfos[0];

            /**
             * All infos will be features from the same deck.gl layer. Thus, we can show data for multiple
             * features in the OGI layer, or the plumes layer, etc.
             * This will NOT show data for multiple features in different layers in the same drawer.
             */
            const multiplePrioritizedInfos =
                sortedPickInfos &&
                pickMultiplePrioritizedFilteredLayers(filteredLayersOfInfoAtPoint, prioritizedInfo?.layer.id);

            /**
             * If user clicked a negative emission,
             * they clicked a layer, but got no prioritized info to show
             */
            const clickedNoData = !prioritizedInfo;

            /**
             * Determine if No Data drawer should be opened.
             * No data drawer should open when clicking inside of a capture boundary when no raster data is available.
             * It should not open clicking outside of the boundary at capture level zooms, or when at the target level.
             */
            const isWithinAnyVisibleTarget = visibleCaptures.some((capture) => {
                // target_geometry is missing one level of array nesting for use with turf.polygon
                const captureBounds = capture?.properties?.target_geometry
                    ? [capture?.properties?.target_geometry]
                    : capture?.geometry.coordinates;

                const capturePolygon = polygon(captureBounds);
                return booleanWithin({ coordinates: info.coordinate, type: 'Point' } as Geometry, capturePolygon);
            });

            const isRasterLayerVisible = flooredZoom >= ZOOM_THRESHOLDS.MINIMUM_ZOOM_LEVEL_AREA_FLUX_GRID;

            const showNoDataDrawer = isRasterLayerVisible && isWithinAnyVisibleTarget;

            const deckWidth = deck.current.width;
            const repositionMap =
                deckWidth && // have a deck element to work with
                (info?.x ?? 0) > deckWidth + rightWidthOffset; // feature is hidden behind the drawer

            // call handleMapInfo - opens the info drawer - with the info that was clicked.
            if (!clickedNoData && flooredZoom >= maxZoomOGITiles - 1 && multiplePrioritizedInfos) {
                handleMapInfo?.(multiplePrioritizedInfos, clickedNoData, showNoDataDrawer, true);
            } else if (sortedPickInfos && sortedPickInfos.length > 0) {
                handleMapInfo?.(sortedPickInfos, clickedNoData, showNoDataDrawer, false);
            } else {
                handleMapInfo?.(undefined, clickedNoData, showNoDataDrawer);
            }

            /**
             * Set the selected feature in the redux store.
             *
             * This is used for keeping track of the feature across components, allowing us to do things
             * like add special highlights or query for a feature from the URL.
             */

            /**  A single OGI infrastructure point was clicked */
            if (
                prioritizedInfo &&
                isPointInfrastructureInfo(prioritizedInfo) &&
                !isClusteredPointInfrastructureInfo(prioritizedInfo)
            ) {
                dispatch(setSelectedOGIFeature(prioritizedInfo.object));
            } else if (
                /** A dispersed source, point source, or target was clicked */
                prioritizedInfo &&
                (isRasterInfo(prioritizedInfo) || isPlumeEmissionInfo(prioritizedInfo) || isTargetInfo(prioritizedInfo))
            ) {
                const { featureCoordinates, featureDate, featureTargetId, featureItemId, relatedRasterItemId } =
                    getEmissionSelectionParams(prioritizedInfo);
                const featureUrlDateString = formatDateForUrl(new Date(featureDate));

                // TODO: This can be deprecated eventually
                // https://methanesat.atlassian.net/browse/DP-5699
                dispatch(
                    setSelectedFeatureParams({
                        coordinates: featureCoordinates,
                        targetDate: featureUrlDateString,
                        targetId: featureTargetId,
                        itemId: featureItemId
                    })
                );

                // To set an active capture (which highlights the entire capture associated with a point source),
                // we need to get the item ID that belongs to the Portal STAC capture.
                // For point sources, this is `relatedRasterItemId`, not their own `itemId`
                dispatch(
                    setActiveCapture({
                        targetId: featureTargetId,
                        itemId: relatedRasterItemId ?? featureItemId
                    })
                );
            }
            if (repositionMap) {
                // prevent the repositioning if clicking on no data AND not showing the no data drawer
                if (!clickedNoData || (clickedNoData && showNoDataDrawer)) {
                    // reposition longitudinally only
                    const recenteredViewState: ViewStatePropsQueryString = {
                        ...viewState,
                        longitude: prioritizedInfo?.coordinate?.[0] ?? info.coordinate?.[0] ?? viewState.longitude
                    };
                    dispatch(setEmissionsMapViewState(recenteredViewState as ViewStatePropsQueryString));
                }
            }
        };
    };
}

// check if there is only one card available with one accordion and
// return the ID so that that accordion can be set to open by default
export const checkForSingleCard = (cardData: EmissionDrawerCardData[]) => {
    if (cardData && cardData.length === 1) {
        const { dispersedSourceEmissions, distinctPointEmissions } = cardData[0];
        //only true if one of them is length 1
        if ((dispersedSourceEmissions.length === 1) !== (distinctPointEmissions.length === 1)) {
            //return correct format for accordion id depending on data
            if (dispersedSourceEmissions.length) {
                return dispersedSourceEmissions[0]?.layer?.id;
            }

            return `${distinctPointEmissions[0]?.layer?.id}_${distinctPointEmissions[0].object.properties.plume_id}`;
        }

        return null;
    }

    return null;
};
